import { useEffect, useState, useCallback } from "react";
import { Eye } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHead, TableSkeleton, StatusBadge, Pager, Money } from "./ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const PS = 20;
const TABS = ["All", "Pending", "Confirmed", "In Progress", "Completed", "Cancelled", "Refunded"];

export default function AdminBookings() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("All");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams({ page, page_size: PS, status: tab });
    if (search) p.set("search", search);
    setData(null);
    api.get(`/admin/bookings?${p}`).then((r) => setData(r.data));
  }, [page, tab, search]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setDetail({ loading: true });
    const r = await api.get(`/admin/bookings/${id}`);
    setDetail(r.data);
  };
  const releaseEscrow = async (id) => {
    try {
      const r = await api.patch(`/admin/bookings/${id}/escrow`);
      toast.success(`Escrow advanced to ${r.data.escrow_status}`);
      openDetail(id); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const changeStatus = async (id, s) => {
    await api.patch(`/admin/bookings/${id}/status`, { status: s });
    toast.success(`Booking ${s}`);
    openDetail(id); load();
  };

  return (
    <div data-testid="admin-bookings">
      <PageHead title="Bookings" subtitle="Track and manage all bookings" />
      <div className="flex flex-wrap gap-1 mb-4">
        {TABS.map((t) => (
          <button key={t} onClick={() => { setPage(1); setTab(t); }} data-testid={`booking-tab-${t.replace(" ", "-").toLowerCase()}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${tab === t ? "bg-[#0D5C75] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{t}</button>
        ))}
      </div>
      <Input placeholder="Search patient / provider / id…" value={search} data-testid="booking-search"
        onChange={(e) => { setPage(1); setSearch(e.target.value); }} className="max-w-xs mb-4" />

      <div className="rounded-2xl bg-white p-4 shadow-sm border border-black/5">
        {!data ? <TableSkeleton /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Booking</TableHead><TableHead>Patient</TableHead><TableHead>Provider</TableHead>
                <TableHead>Package</TableHead><TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead><TableHead>Escrow</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.bookings.map((b) => (
                  <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                    <TableCell className="font-mono text-xs">{b.id.slice(0, 10)}</TableCell>
                    <TableCell>{b.patient_name || "—"}</TableCell>
                    <TableCell className="max-w-[160px] truncate">{b.provider_name}</TableCell>
                    <TableCell className="max-w-[150px] truncate">{b.package?.name}</TableCell>
                    <TableCell className="text-right"><Money value={b.amount} /></TableCell>
                    <TableCell><StatusBadge status={b.display_status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{b.escrow_status || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openDetail(b.id)} data-testid={`booking-view-${b.id}`}><Eye size={15} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {data.bookings.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No bookings.</TableCell></TableRow>}
              </TableBody>
            </Table>
            <Pager page={page} total={data.total} pageSize={PS} onPage={setPage} />
          </div>
        )}
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="booking-detail-panel">
          <SheetHeader><SheetTitle>Booking detail</SheetTitle></SheetHeader>
          {detail && !detail.loading && (
            <div className="mt-4 space-y-4 text-sm">
              <div className="rounded-xl bg-gray-50 p-3">
                <div className="font-semibold text-[#111827]">{detail.patient?.name || detail.patient_name}</div>
                <div className="text-muted-foreground text-xs">{detail.patient?.email}</div>
              </div>
              <div><span className="text-muted-foreground">Provider:</span> {detail.provider_name}</div>
              <div><span className="text-muted-foreground">Package:</span> {detail.package?.name} · <Money value={detail.split?.tcv} /></div>
              <div><StatusBadge status={detail.display_status} /></div>
              <div>
                <div className="font-semibold text-[#111827] mb-2">Escrow milestones</div>
                <div className="flex gap-1">
                  {detail.milestones?.map((m) => {
                    const done = detail.milestones.indexOf(detail.escrow_status) >= detail.milestones.indexOf(m);
                    return <div key={m} className={`flex-1 h-2 rounded-full ${done ? "bg-[#2A9D8F]" : "bg-gray-200"}`} title={m} />;
                  })}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Current: {detail.escrow_status}</div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button size="sm" onClick={() => releaseEscrow(detail.id)} data-testid="release-escrow-btn"
                  className="bg-[#2A9D8F] hover:bg-[#238377] text-white">Release next milestone</Button>
                <Button size="sm" variant="outline" onClick={() => changeStatus(detail.id, "Cancelled")} data-testid="cancel-booking-btn">Cancel</Button>
                <Button size="sm" variant="outline" onClick={() => changeStatus(detail.id, "Refunded")} data-testid="refund-booking-btn">Refund</Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
